import {
  EmailSignInClient,
  type EmailSignInState,
  initialEmailSignInState,
  isSignInBusy,
  resendSecondsRemaining,
} from './email-sign-in';

export function escapeSignInText(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[character]!);
}

function statusText(state: EmailSignInState): string {
  if (state.phase === 'sending') return 'Sending your sign-in email…';
  if (state.phase === 'verifying') return 'Signing you in…';
  return state.lastSentAt === null ? '' : 'Email sent. Use the latest email to sign in.';
}

function resendLabel(seconds: number): string {
  return seconds > 0 ? `Resend Email (${seconds}s)` : 'Resend Email';
}

function renderFields(state: EmailSignInState, now: number): string {
  const busy = isSignInBusy(state);
  const disabled = busy ? ' disabled' : '';
  const codeEntry = state.lastSentAt !== null;
  const remaining = resendSecondsRemaining(state, now);
  return `
    ${codeEntry ? `
      <p class="card-copy">We sent a code and magic link to <strong>${escapeSignInText(state.email)}</strong>.</p>
      <p id="auth-instructions" class="auth-note">Enter the code here, or use the link in your email. On iPhone/iPad, enter the code in your Home Screen app to stay signed in there.</p>
    ` : '<p class="card-copy">Enter your email to get started. We’ll send a login code and a magic link. No password needed.</p>'}
    <form id="${codeEntry ? 'auth-code-form' : 'auth-form'}" class="auth-form" aria-busy="${busy}">
      <label class="field">
        <span>${codeEntry ? 'Email code' : 'Email'}</span>
        ${codeEntry
          ? `<input id="auth-code" name="code" type="text" inputmode="numeric" autocomplete="one-time-code" required aria-describedby="auth-instructions auth-error"${disabled} />`
          : `<input id="auth-email" name="email" type="email" autocomplete="email" maxlength="320" value="${escapeSignInText(state.email)}" required aria-describedby="auth-error"${disabled} />`}
      </label>
      <button class="btn" type="submit"${disabled}>${codeEntry ? 'Sign In' : 'Send Sign-In Email'}</button>
    </form>
    <p id="auth-status" class="auth-note" role="status" aria-live="polite">${statusText(state)}</p>
    <p id="auth-error" class="auth-error" role="alert" aria-live="assertive">${escapeSignInText(state.error ?? '')}</p>
    ${codeEntry ? `
      <div class="auth-actions">
        <button id="auth-resend" class="btn btn-secondary" type="button"${busy || remaining > 0 ? ' disabled' : ''}>${resendLabel(remaining)}</button>
        <button id="auth-change-email" class="btn btn-secondary" type="button"${disabled}>Use a Different Email</button>
      </div>
    ` : ''}
  `;
}

export function renderSignInForm(state = initialEmailSignInState(), now = Date.now()): string {
  return `<div data-email-sign-in>${renderFields(state, now)}</div>`;
}

/** Mount only the auth card; cooldown updates never replace the typed code or focus. */
export function mountSignInForm(
  element: HTMLElement,
  client: EmailSignInClient,
  getRedirectTo: () => string,
): () => void {
  let pane = '';
  let lastSentAt: number | null = null;
  const update = () => {
    const state = client.getState();
    const codeEntry = state.lastSentAt !== null;
    const nextPane = codeEntry ? `code:${state.email}` : 'email';
    if (nextPane !== pane) {
      const wasMounted = pane !== '';
      pane = nextPane;
      element.innerHTML = renderFields(state, Date.now());
      if (wasMounted) element.querySelector<HTMLInputElement>('input')?.focus();
    }
    const busy = isSignInBusy(state);
    const input = element.querySelector<HTMLInputElement>('input');
    if (input) {
      input.disabled = busy;
      input.setAttribute('aria-invalid', String(Boolean(state.error)));
      if (codeEntry && state.lastSentAt !== lastSentAt) input.value = '';
    }
    lastSentAt = state.lastSentAt;
    element.querySelector('form')?.setAttribute('aria-busy', String(busy));
    element.querySelectorAll<HTMLButtonElement>('button').forEach((button) => { button.disabled = busy; });
    const resend = element.querySelector<HTMLButtonElement>('#auth-resend');
    if (resend) {
      const remaining = resendSecondsRemaining(state, Date.now());
      resend.disabled = busy || remaining > 0;
      resend.textContent = resendLabel(remaining);
    }
    element.querySelector('#auth-status')!.textContent = statusText(state);
    element.querySelector('#auth-error')!.textContent = state.error ?? '';
  };
  const submit = (event: Event) => {
    event.preventDefault();
    const code = element.querySelector<HTMLInputElement>('#auth-code');
    if (code) {
      void client.verify(code.value);
    } else {
      const email = element.querySelector<HTMLInputElement>('#auth-email');
      if (email) void client.send(email.value, getRedirectTo());
    }
  };
  const click = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('#auth-resend')) void client.send('', getRedirectTo());
    if (target.closest('#auth-change-email')) client.useDifferentEmail();
  };
  const input = () => {
    const email = element.querySelector<HTMLInputElement>('#auth-email');
    if (email) client.setEmailDraft(email.value);
  };
  element.addEventListener('submit', submit);
  element.addEventListener('click', click);
  element.addEventListener('input', input);
  const unsubscribe = client.subscribe(update);
  const timer = window.setInterval(update, 1000);
  update();
  return () => {
    unsubscribe();
    window.clearInterval(timer);
    element.removeEventListener('submit', submit);
    element.removeEventListener('click', click);
    element.removeEventListener('input', input);
  };
}
